/**
 * Everything the editor's surfaces can do, in one bag.
 *
 * The menu bar, the palette, the inspector and the status bar all want the same handful of
 * facts about the document and the same handful of verbs over it. Passing them as one value
 * keeps each component presentational -- it renders what it is given and calls what it is
 * handed -- and keeps the page the only place where an action is actually wired to the
 * editor, the player or the browser.
 *
 * `edit` is the single door onto the document. Every editing layer in the package answers
 * with a command or with a sentence saying why there is not one, and `edit` takes both: the
 * page applies the command or shows the sentence, so no component has to decide what a
 * refusal looks like.
 */

import { engrave, selection as select } from "@app/score-editor";
import type {
  Caret,
  Command,
  EntryInput,
  EntryState,
  PartId,
  Score,
  Selection,
  TimeRange,
} from "@app/score-editor";
import type { ScoreShortcut } from "./score-keys";
import type { ScoreMidi } from "./use-score-midi";

export interface ScoreActions {
  readonly score: Score;
  readonly selection: Selection;
  readonly entry: EntryState;
  readonly caret: Caret | null;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** What undo and redo would do, for the menu item that offers them. */
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;
  /** Whether anything has been copied, which is what makes Paste worth offering. */
  readonly hasClip: boolean;
  readonly playing: boolean;
  /**
   * The recordings this score is scored for are still being fetched, and a press of play is
   * waiting for them. The press is held rather than refused, so this only changes what the
   * button says: it is the one moment pressing play does nothing visible.
   */
  readonly loading: boolean;
  readonly metronome: boolean;
  readonly countIn: boolean;
  /**
   * Whether the marks are played as well as the notes.
   *
   * On, a staccato dot shortens its note, a hairpin slopes between two dynamics, a trill
   * and a tremolo sound as the notes they stand for, an arpeggio is spread and a fermata is
   * held. Off is the written notes flat, which is what checking what you wrote wants.
   */
  readonly marks: boolean;
  /** The stretch being repeated, or `null` when the piece plays through. */
  readonly loop: TimeRange | null;
  /** Verovio's own scale, as a percentage of its natural size. */
  readonly scale: number;
  /** Whether the music is drawn on the page it is set on rather than across the desk. */
  readonly paper: boolean;
  /** Whether the editor has the whole screen, the app's own frame put away. */
  readonly fullscreen: boolean;
  /**
   * Whether the score on the screen is the concert-pitch reading of the document.
   *
   * The document itself is always the written one. What this changes is what is drawn,
   * printed and exported as MusicXML, and what a typed letter means: in this reading a `C`
   * typed into a clarinet part is the C that sounds.
   */
  readonly concert: boolean;

  /**
   * The MIDI keyboard: which ones there are, which one is writing, and how to change that.
   *
   * It is one value rather than four flags because it is one thing -- a device that is
   * either attached and listened to or not -- and because the menu and the status bar both
   * want all of it.
   */
  readonly keyboard: ScoreMidi;

  /** Apply a built edit, or show the sentence saying why there is none. */
  edit(built: Command | string, label?: string): void;
  /** Let the note-entry state machine answer for one thing the user did. */
  type(input: EntryInput): void;
  select(selection: Selection): void;
  undo(): void;
  redo(): void;
  copy(): void;
  cut(): void;
  paste(): void;
  newScore(): void;
  openFile(): void;
  save(): void;
  exportXml(): void;
  /**
   * Save the performance as a MIDI file.
   *
   * It has no shortcut where MusicXML has one because they are not the same errand: a
   * score is exchanged with another engraver often, and handed to a sampler rarely.
   */
  exportMidi(): void;
  /** Engrave the score onto its own paper and hand it to the browser's print dialog. */
  print(): void;
  /** Save one part as a score of its own, which is the copy a player is given. */
  savePart(id: PartId): void;
  /**
   * Ask for a chord symbol to be typed over the selection.
   *
   * A chord is text and the text is typed in the inspector, so this opens that panel and
   * puts the caret in its box rather than editing anything itself. It stays an action like
   * the others because ⌘K is how a chart is written and the key has to reach something.
   */
  writeChord(): void;
  /**
   * Ask for a stack of figures to be typed under the selection.
   *
   * The twin of `writeChord`, and for the same reason: a figure is text, the text is typed
   * in the inspector, and ⌘G has to reach something. Separate from it because the two boxes
   * are separate -- a continuo part carries both at once, over and under the same bass.
   */
  writeFigures(): void;
  /**
   * Move what is selected, in tenths of a staff space, rightwards and upwards.
   *
   * The one thing in the editor that changes how the music looks without changing what it
   * says, which is why it is a verb of its own rather than another mark: the engraver has
   * already placed the note and this is the person writing disagreeing with it.
   */
  nudge(x: number, y: number): void;
  /** Put the selection back where the engraver had it. */
  unnudge(): void;
  /** Play from the selection when there is one, else from where the position stands. */
  playPause(): void;
  stop(): void;
  toggleMetronome(): void;
  toggleCountIn(): void;
  /** Swap between playing what the marks ask for and playing the notes flat. */
  toggleMarks(): void;
  /** Repeat the selected bars, or stop repeating. */
  toggleLoop(): void;
  zoom(by: number): void;
  /** Swap between the paper the score is set on and the width of the desk. */
  togglePaper(): void;
  toggleFullscreen(): void;
  /** Swap between the score as its players read it and the score as it sounds. */
  toggleConcert(): void;
  /**
   * Start another line of music on the staff the caret is on.
   *
   * It is made in the bars the selection covers, or in the caret’s bar when nothing is
   * selected, and the caret comes out standing in it. A voice belongs to a bar rather than
   * to the whole staff, which is why a passage is selected first.
   */
  addVoice(): void;
  /** Take the caret’s voice off the staff again, with everything hung on it. */
  removeVoice(): void;
  /**
   * Offer an ossia above the staff the caret is on: another way to play the bar.
   *
   * It covers the bars the selection covers, or the caret’s bar when nothing is selected,
   * and it starts out holding a copy of the line it is an alternative to. It is drawn
   * small on a staff of its own above that one, and it is never played.
   */
  addOssia(): void;
  /** Take the ossia off the caret’s staff again, with everything hung on it. */
  removeOssia(): void;
  /**
   * Draw the selection on the staff `delta` away, its voice keeping it.
   *
   * This is the cross-staff note a keyboard part is full of: what moves is where the note
   * is drawn, not which line of music it belongs to, so the beam still crosses to it and
   * the bar it is counted in is unchanged.
   */
  crossStaff(delta: number): void;
  /** Draw the selection on its own voice's staff again. */
  uncrossStaff(): void;
  toggleHelp(): void;
}

/**
 * What each keyboard shortcut runs.
 *
 * The table is here rather than in the page because it is the bridge between the two halves
 * that already exist -- the keys, which know nothing about a score, and the actions, which
 * know nothing about keys -- and because a switch in a key handler is where shortcuts go
 * missing.
 */
export const SHORTCUT_ACTIONS: Readonly<Record<ScoreShortcut, (actions: ScoreActions) => void>> = {
  undo: (actions) => actions.undo(),
  redo: (actions) => actions.redo(),
  copy: (actions) => actions.copy(),
  cut: (actions) => actions.cut(),
  paste: (actions) => actions.paste(),
  selectAll: (actions) => actions.select(select.selectAll(actions.score)),
  chord: (actions) => actions.writeChord(),
  figures: (actions) => actions.writeFigures(),
  nudgeUp: (actions) => actions.nudge(0, engrave.NUDGE_STEP),
  nudgeDown: (actions) => actions.nudge(0, -engrave.NUDGE_STEP),
  nudgeLeft: (actions) => actions.nudge(-engrave.NUDGE_STEP, 0),
  nudgeRight: (actions) => actions.nudge(engrave.NUDGE_STEP, 0),
  open: (actions) => actions.openFile(),
  save: (actions) => actions.save(),
  exportXml: (actions) => actions.exportXml(),
  print: (actions) => actions.print(),
  zoomIn: (actions) => actions.zoom(6),
  zoomOut: (actions) => actions.zoom(-6),
  playPause: (actions) => actions.playPause(),
  stop: (actions) => actions.stop(),
  loop: (actions) => actions.toggleLoop(),
  metronome: (actions) => actions.toggleMetronome(),
  addVoice: (actions) => actions.addVoice(),
  removeVoice: (actions) => actions.removeVoice(),
  crossUp: (actions) => actions.crossStaff(-1),
  crossDown: (actions) => actions.crossStaff(1),
  help: (actions) => actions.toggleHelp(),
};
